import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';
import { faIR } from '@mui/material/locale';

/**
 * The visual identity.
 *
 * The brief was a system that reads as belonging to a genuinely high-tech engineering
 * organization. What that means concretely, and what this palette is built on:
 *
 * - **Dark by default.** This is an operations tool people keep open all day next to an
 *   editor and a terminal. A bright surface among dark ones is the thing you close.
 * - **Near-black with a blue cast, not grey.** `#0B1120` reads as engineered rather than
 *   drab, and it gives coloured status a surface to sit on without vibrating.
 * - **One accent, used sparingly.** Cyan carries interaction and nothing else. A palette
 *   where five things are emphasised has emphasised nothing.
 * - **Status colours are never the only signal.** Health and state always pair colour with
 *   a word or an icon, because roughly one in twelve men cannot separate the red from the
 *   green, and because `frontend-standards.md` section 12 requires it.
 */

/** Deep, slightly blue near-black. The ground everything else sits on. */
const surface = {
  base: '#0B1120',
  raised: '#111A2E',
  overlay: '#18233D',
  border: '#22304F',
  borderStrong: '#31426B',
} as const;

/** Cyan. Interaction only - never decoration, never status. */
const accent = {
  main: '#22D3EE',
  dark: '#0E9AAF',
  light: '#67E8F9',
  contrast: '#04121A',
} as const;

/**
 * Workflow and health colours.
 *
 * Chosen for legibility on the dark ground rather than for vividness: saturated pure hues
 * shimmer against near-black, so each is desaturated slightly and lifted in lightness.
 */
export const statusPalette = {
  backlog: '#7C8DB5',
  planned: '#60A5FA',
  inProgress: '#34D399',
  inReview: '#FBBF24',
  done: '#4ADE80',
  blocked: '#F87171',
} as const;

export type StatusKey = keyof typeof statusPalette;

const shared: ThemeOptions = {
  direction: 'rtl',
  shape: { borderRadius: 10 },
  typography: {
    // Vazirmatn is the Persian face; the Latin fallbacks only ever render digits and code.
    fontFamily: '"Vazirmatn", "Inter", system-ui, -apple-system, "Segoe UI", sans-serif',
    fontSize: 14,
    h1: { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontSize: '1.375rem', fontWeight: 600, letterSpacing: '-0.01em' },
    h3: { fontSize: '1.125rem', fontWeight: 600 },
    h4: { fontSize: '1rem', fontWeight: 600 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.7 },
    body2: { fontSize: '0.875rem', lineHeight: 1.65 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
    caption: { fontSize: '0.75rem', lineHeight: 1.6 },
  },
};

export const darkTheme: Theme = createTheme(
  {
    ...shared,
    palette: {
      mode: 'dark',
      primary: {
        main: accent.main,
        dark: accent.dark,
        light: accent.light,
        contrastText: accent.contrast,
      },
      secondary: { main: '#A78BFA' },
      background: { default: surface.base, paper: surface.raised },
      text: {
        primary: '#E8EDF7',
        secondary: '#9AAAC8',
        disabled: '#5C6B8A',
      },
      divider: surface.border,
      success: { main: statusPalette.done },
      warning: { main: statusPalette.inReview },
      error: { main: statusPalette.blocked },
      info: { main: statusPalette.planned },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundImage:
              // A single, very soft radial lift behind the header. It stops a full-bleed
              // near-black page from looking like a rendering failure, without becoming
              // decoration anyone has to look at.
              'radial-gradient(1200px 400px at 50% -140px, rgba(34, 211, 238, 0.08), transparent 70%)',
            backgroundAttachment: 'fixed',
          },
          // Persian text with Latin digits looks wrong; a tabular figure keeps columns of
          // numbers aligned, which matters in every table in this product.
          '.tabular': {
            fontFeatureSettings: '"tnum" 1',
            fontVariantNumeric: 'tabular-nums',
          },
          '.mono': {
            fontFamily: '"JetBrains Mono", ui-monospace, "Cascadia Code", monospace',
            fontFeatureSettings: '"tnum" 1',
          },
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: surface.borderStrong,
            borderRadius: 8,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${surface.border}`,
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'transparent' },
        styleOverrides: {
          root: {
            backdropFilter: 'blur(12px)',
            backgroundColor: 'rgba(11, 17, 32, 0.82)',
            borderBottom: `1px solid ${surface.border}`,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8, paddingInline: 16 },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, fontSize: '0.75rem' },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small', fullWidth: true },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottomColor: surface.border },
          head: {
            fontWeight: 600,
            color: '#9AAAC8',
            backgroundColor: surface.overlay,
            whiteSpace: 'nowrap',
          },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
      },
      MuiLink: {
        defaultProps: { underline: 'hover' },
      },
    },
  },
  faIR,
);

/**
 * The light theme.
 *
 * Kept deliberately close in structure so a component styled for one is not broken in the
 * other. It exists because meeting rooms and projectors are real, not because anyone will
 * use it at a desk.
 */
export const lightTheme: Theme = createTheme(
  {
    ...shared,
    palette: {
      mode: 'light',
      primary: { main: '#0E7490', dark: '#155E75', light: '#22D3EE', contrastText: '#FFFFFF' },
      secondary: { main: '#7C3AED' },
      background: { default: '#F5F7FB', paper: '#FFFFFF' },
      text: { primary: '#0F172A', secondary: '#475569' },
      divider: '#DFE5F0',
      success: { main: '#15803D' },
      warning: { main: '#B45309' },
      error: { main: '#B91C1C' },
      info: { main: '#1D4ED8' },
    },
    components: {
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none', border: '1px solid #E4E9F2' } },
      },
      MuiButton: { defaultProps: { disableElevation: true } },
      MuiTextField: { defaultProps: { size: 'small', fullWidth: true } },
    },
  },
  faIR,
);
